/**
 * Rogue Alloy van-life package (26 September 2026).
 *
 * Owns the finish explorer's chip filtering
 * (sections/rogue-finish-explorer.liquid). Framework-free ES module.
 *
 * Progressive enhancement: every photo card is rendered visible in the
 * markup with the "All" chip already `aria-pressed="true"`, so a reader
 * with no JavaScript sees every approved photo and the chips simply do
 * nothing - never an empty grid. This script only ever shows or hides
 * cards by toggling the `hidden` attribute (no animation, no opacity
 * transition), so there is nothing for `prefers-reduced-motion` to need to
 * turn off: the filter is instant for every visitor, motion preference or
 * not.
 *
 * Unit tested by tests/rogue-vanlife.test.js.
 */

/**
 * Whether a card's finish should be visible under the selected chip.
 * @param {string} cardFinish - a card's `data-rogue-finish` value, e.g. "Black".
 * @param {string} selectedFinish - the pressed chip's `data-rogue-finish` value,
 *   or "all" for the "All" chip.
 * @returns {boolean}
 */
export function matchesFinish(cardFinish, selectedFinish) {
  if (typeof selectedFinish !== 'string') return true;
  if (selectedFinish.toLowerCase() === 'all') return true;
  if (typeof cardFinish !== 'string') return false;
  return cardFinish.trim().toLowerCase() === selectedFinish.trim().toLowerCase();
}

/**
 * Builds the on-site search URL for "wheels in this finish" - the site has
 * no cross-model colour/finish collection or reliable option-value filter
 * to link to (wheel colour values are things like "SATIN BLACK BRONZE
 * LIP", not a plain "Black"), so a plain-text search is the honest, always-
 * working choice rather than a filter link that could 404 or return
 * nothing.
 * @param {string} finish - e.g. "Black".
 * @returns {string}
 */
export function buildFinishSearchUrl(finish) {
  const query =
    typeof finish === 'string' && finish.trim() !== '' ? `${finish.trim()} wheel` : 'wheel';
  return `/search?q=${encodeURIComponent(query)}`;
}

/**
 * Wires up every `[data-rogue-finish-explorer]` block on the page: clicking
 * a chip sets it (and only it) `aria-pressed="true"`, and shows every card
 * whose `data-rogue-finish` matches - or every card, for the "All" chip.
 * Keyboard users get this for free: every chip is a plain `<button>`, a
 * native tab stop operable with Enter or Space, no roving tabindex needed
 * for a row this short.
 * @param {Document|Element} [root]
 */
export function initFinishExplorer(root = document) {
  if (typeof root.querySelectorAll !== 'function') return;

  root.querySelectorAll('[data-rogue-finish-explorer]').forEach((explorer) => {
    const chips = Array.from(explorer.querySelectorAll('[data-rogue-finish-chip]'));
    const cards = Array.from(explorer.querySelectorAll('[data-rogue-finish-card]'));
    if (chips.length === 0 || cards.length === 0) return;

    const applyFilter = (selected) => {
      cards.forEach((card) => {
        const cardFinish = card.getAttribute('data-rogue-finish-card') || '';
        card.hidden = !matchesFinish(cardFinish, selected);
      });
    };

    chips.forEach((chip) => {
      chip.addEventListener('click', () => {
        const selected = chip.getAttribute('data-rogue-finish-chip') || 'all';
        chips.forEach((other) => other.setAttribute('aria-pressed', String(other === chip)));
        applyFilter(selected);
      });
    });
  });
}

if (typeof document !== 'undefined') {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initFinishExplorer());
  } else {
    initFinishExplorer();
  }
}
